import { CoreGraphSection, TkbUsageBar } from '@components'
import { breakpoints } from '@constants'
import { AccountsSection, DashboardBanner, DashboardTransactions } from '@dashboard/components'
import CardsImage from '@images/cards-banner.png'
import InviteImage from '@images/invite-banner.png'
import SavingsImage from '@images/savings-banner.png'
import cn from 'classnames'
import { paths, selectMerchantCapabilities, useAppSelector } from 'mmfintech-backend-api'
import { tr, useMatchMedia } from 'mmfintech-commons'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import './AdminDashboard.scss'

const AdminDashboard = () => {
  const [collapsedMenu, setCollapsedMenu] = useState(false)
  const [collapse, setCollapse] = useState(false)

  const isMobile = useMatchMedia({
    breakpoint: breakpoints.MAX_MOBILE_WIDTH,
    prefix: 'max'
  })

  const history = useHistory()

  const capabilities = useAppSelector(selectMerchantCapabilities)

  const handleOrderClick = () => {
    history.push(paths.cards.cardOrder())
  }

  const fixResponsiveness = () => {
    collapsedMenu
      ? setTimeout(() => {
          setCollapse(true)
        }, 500)
      : setCollapse(false)
  }

  useEffect(() => {
    if (!isMobile) {
      fixResponsiveness()
    }
  }, [collapsedMenu])

  useEffect(() => {
    const targetNode = document.querySelector('.core-menu-wrapper')

    if (!targetNode) return

    if (targetNode.classList.contains('collapsed')) {
      setCollapsedMenu(true)
    }

    const callback = mutationList => {
      for (const mutation of mutationList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          setCollapsedMenu(mutation.target.classList.contains('collapsed'))
        }
      }
    }

    const observer = new MutationObserver(callback)
    observer.observe(targetNode, {
      attributes: true,
      childList: false,
      subtree: false
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div className='admin-dashboard-wrapper' data-test='admin-dashboard'>
      <div className='admin-dashboard-left-section'>
        <CoreGraphSection />
        <DashboardTransactions />
      </div>

      <div
        className={cn('admin-dashboard-right-section', {
          collapsed: collapse && !isMobile
        })}
      >
        <AccountsSection />

        {/* SDK's MerchantCapabilities type lags the BFF — `enableTkb` exists on the
            wire but not yet in the typings, so we read it through an `any` cast. */}
        {Boolean((capabilities as any)?.enableTkb) && <TkbUsageBar />}

        {capabilities?.enableCardIssuing ? (
          <DashboardBanner
            heading={tr('FRONTEND.DASHBOARD.BANNER.CARDS.HEADING', 'Cards')}
            image={CardsImage}
            title={tr('FRONTEND.DASHBOARD.BANNER.CARDS.TITLE', 'The Kingdom Bank cards')}
            description={tr(
              'FRONTEND.DASHBOARD.BANNER.CARDS.CONTENT',
              'Use your The Kingdom Bank physical or virtual debit card for seamless purchases at retail stores, online platforms, and ATMs.'
            )}
            redirect={() => handleOrderClick()}
            buttonLabel={tr('FRONTEND.DASHBOARD.BUTTONS.ORDER_CARD', 'Order card')}
          />
        ) : (
          <DashboardBanner
            heading={tr('FRONTEND.DASHBOARD.BANNER.SAVINGS.HEADING', 'Savings')}
            image={SavingsImage}
            title={tr('FRONTEND.DASHBOARD.BANNER.SAVINGS.TITLE', 'Explore our saving plans')}
            description={tr(
              'FRONTEND.DASHBOARD.BANNER.SAVINGS.CONTENT',
              'It’s the extra earnings you want from a savings account with the flexibility you need — all in one place.'
            )}
            redirect={paths.more.savings()}
            buttonLabel={tr('FRONTEND.DASHBOARD.BUTTONS.VIEW_MORE', 'View more')}
          />
        )}

        <DashboardBanner
          image={InviteImage}
          heading={tr('FRONTEND.DASHBOARD.BANNER.INVITE.HEADING', 'Invite & Earn')}
          title={tr('FRONTEND.DASHBOARD.BANNER.INVITE.TITLE', 'Explore our Referral Programs')}
          description={tr(
            'FRONTEND.DASHBOARD.BANNER.INVITE.CONTENT',
            'Invite and earn a commission for each person who registers in The Kingdom Bank.'
          )}
          redirect={paths.referralProgram()}
          buttonLabel={tr('FRONTEND.DASHBOARD.BUTTONS.VIEW_MORE', 'View more')}
        />
      </div>
    </div>
  )
}

export default AdminDashboard
